// First screen after logging in: a welcome message, the open-games browser,
// joining by room code, and creating a game. Icons at the top open the
// locker, shop and settings. The coin total is shown here as a whole number.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  ROOM_CODE_LENGTH,
  RoomCodeSchema,
  type CreateLobbyBody,
  type LobbySettingsPatch,
  type LobbySummary,
  type Me,
} from "@wp/shared";
import { formatCoins } from "../../lib/format";
import { setPendingSettings } from "../../lib/pendingSettings";
import { api } from "../../net/api";
import { RoomBackdrop } from "../common/RoomBackdrop";
import { Icon } from "../common/Icon";
import { Modal } from "../common/Modal";
import { LockerWindow } from "../common/LockerWindow";
import { MenuTip } from "../common/FirstTimeTips";
import { SettingsWindow, type SettingsTab } from "../common/SettingsWindow";
import { ShopWindow } from "../common/ShopWindow";
import { Tip } from "../common/Tip";
import { CreateGameWindow } from "./CreateGameWindow";
import { JoinPrivateDialog } from "./LobbyDialogs";
import { WeeklyRewardsButton } from "./WeeklyRewards";

type MenuWindow = { kind: "none" } | { kind: "settings"; tab: SettingsTab } | { kind: "shop" | "create" | "locker" };

const LIST_REFRESH_MS = 15_000;

interface Props {
  me: Me;
  onMeChange: (me: Me) => void;
  onEnterLobby: (lobbyId: string) => void;
  onLogout: () => void;
  /** False inside Discord: the Discord Activity has no logout. */
  canLogout?: boolean;
  /** Discord Activity: go to the table everyone in this Activity shares. Resolves null, or why it failed. */
  onVoiceTable?: (() => Promise<string | null>) | null;
  notice: string | null;
  onDismissNotice: () => void;
}

export function WelcomeScreen({
  me,
  onMeChange,
  onEnterLobby,
  onLogout,
  canLogout = true,
  onVoiceTable = null,
  notice,
  onDismissNotice,
}: Props) {
  const [voiceTableError, setVoiceTableError] = useState<string | null>(null);
  const [voiceTableBusy, setVoiceTableBusy] = useState(false);
  const [lobbies, setLobbies] = useState<LobbySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [joining, setJoining] = useState<string | null>(null);
  const [privateLobby, setPrivateLobby] = useState<{ id: string; name: string } | null>(null);
  const [win, setWin] = useState<MenuWindow>({ kind: "none" });
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  /** Admin: the lobby waiting for "Close it?" to be confirmed, and whether the request is out. */
  const [closing, setClosing] = useState<{ lobby: LobbySummary; busy: boolean; error: string | null } | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const res = await api.listLobbies();
    setLoading(false);
    if (res.ok) {
      setLobbies(res.value);
      setListError(null);
    } else {
      setListError(res.error.message);
    }
  }, []);

  const refreshMe = useCallback(async () => {
    const res = await api.me();
    if (res.ok) onMeChange(res.value);
  }, [onMeChange]);

  useEffect(() => {
    void refresh();
    void refreshMe();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, LIST_REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh, refreshMe]);

  async function join(lobby: { id: string; name: string; isPrivate: boolean }) {
    // Admins walk past the password.
    if (lobby.isPrivate && !me.isAdmin) {
      setPrivateLobby({ id: lobby.id, name: lobby.name });
      return;
    }
    setJoining(lobby.id);
    const res = await api.joinLobby(lobby.id);
    setJoining(null);
    if (res.ok) onEnterLobby(lobby.id);
    else {
      setListError(res.error.message);
      void refresh();
    }
  }

  /** Admin: close the confirmed lobby, then reload the list. */
  async function closeLobby() {
    if (!closing || closing.busy) return;
    setClosing({ ...closing, busy: true, error: null });
    const res = await api.adminCloseLobby(closing.lobby.id);
    // Already gone (404) counts as closed.
    if (res.ok || res.error.status === 404) setClosing(null);
    else setClosing({ ...closing, busy: false, error: res.error.message });
    void refresh();
  }

  async function joinPrivate(password: string): Promise<string | null> {
    if (!privateLobby) return null;
    const res = await api.joinLobby(privateLobby.id, password);
    if (!res.ok) return res.error.message;
    onEnterLobby(privateLobby.id);
    return null;
  }

  async function joinByCode(e: FormEvent) {
    e.preventDefault();
    const parsed = RoomCodeSchema.safeParse(code);
    if (!parsed.success) {
      setCodeError(`Room codes are ${ROOM_CODE_LENGTH} letters and numbers.`);
      return;
    }
    setCodeError(null);
    const res = await api.findRoom(parsed.data);
    if (!res.ok) {
      setCodeError(res.error.message);
      return;
    }
    await join(res.value);
  }

  async function create(body: CreateLobbyBody, settings: LobbySettingsPatch): Promise<string | null> {
    const res = await api.createLobby(body);
    if (!res.ok) return res.error.message;
    // Admit ourselves the normal way (a no-op for the creator).
    await api.joinLobby(res.value.id, body.isPrivate ? body.password : undefined);
    setPendingSettings(res.value.id, settings);
    onEnterLobby(res.value.id);
    return null;
  }

  async function logout() {
    await api.logout();
    onLogout();
  }

  return (
    <main className="menu-screen">
      <RoomBackdrop />
      <header className="menu-topbar">
        <span className="purse" data-testid="my-coins-menu">
          <Icon name="coin" size={16} /> {formatCoins(me.coins)}
          <span className="purse-word"> coins</span>
        </span>
        <span className="topbar-icons">
          <WeeklyRewardsButton me={me} onMeChange={onMeChange} onRefresh={refreshMe} />
          <Tip text="Open your locker to wear hats, robes and computer heads you own." side="bottom">
            <button type="button" className="icon-square" onClick={() => setWin({ kind: "locker" })} aria-label="Locker" data-testid="locker-button">
              <Icon name="wizard" />
            </button>
          </Tip>
          <Tip text="Open the shop to look at orbs and cosmetics." side="bottom">
            <button type="button" className="icon-square" onClick={() => setWin({ kind: "shop" })} aria-label="Shop" data-testid="shop-button">
              <Icon name="shop" />
            </button>
          </Tip>
          <Tip text="Change game, audio, accessibility and account settings, or learn how to play." side="bottom">
            <button
              type="button"
              className="icon-square"
              onClick={() => setWin({ kind: "settings", tab: "game" })}
              aria-label="Settings"
              data-testid="settings-button"
            >
              <Icon name="gear" />
            </button>
          </Tip>
          {canLogout && (
            <Tip text="Log out of Wizard Poker on this device." side="bottom">
              <button type="button" className="icon-square" onClick={() => void logout()} aria-label="Log out" data-testid="menu-logout">
                <Icon name="logout" />
              </button>
            </Tip>
          )}
        </span>
      </header>

      <div className="welcome-window window" data-testid="welcome">
        <h1 className="logo">Wizard Poker</h1>
        <p className="logo-sub" aria-hidden="true">// back room · tables online</p>
        <p className="welcome-line">
          Welcome, <strong>{me.displayName}</strong>! Pull up a chair, ready your spells, and try not to go broke.
        </p>
        {notice && (
          <div className="notice" role="status">
            <span>{notice}</span>
            <button type="button" className="icon-btn" aria-label="Dismiss" onClick={onDismissNotice}>
              <Icon name="close" size={14} />
            </button>
          </div>
        )}

        <div className="welcome-grid">
          <section className="game-browser group-box" aria-label="Open games" data-testid="lobby-list">
            <div className="browser-head">
              <h2>Open games</h2>
              <Tip text="Check for new games now." side="left">
                <button type="button" className="btn small" onClick={() => void refresh()} disabled={loading} aria-label="Refresh" data-testid="lobby-refresh">
                  <Icon name="refresh" size={14} /> Refresh
                </button>
              </Tip>
            </div>
            {listError && (
              <p className="form-error" role="alert">
                {listError}
              </p>
            )}
            {lobbies.length === 0 ? (
              <p className="muted">{loading ? "Looking for games…" : "No open games yet. Create one!"}</p>
            ) : (
              <table className="browser-table">
                <thead>
                  <tr>
                    <th scope="col">Game</th>
                    <th scope="col">Host</th>
                    <th scope="col">Seats</th>
                    <th scope="col">Min</th>
                    <th scope="col">Code</th>
                    <th scope="col">
                      <span className="sr-only">{me.isAdmin ? "Join or close" : "Join"}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lobbies.map((l) => (
                    <tr key={l.id} data-testid="lobby-item" data-lobby-id={l.id}>
                      <td>
                        {l.isPrivate && <Icon name="lock" size={12} title="Private" />} {l.name}
                        <span className={`status-pill ${l.status}`}>{l.status === "playing" ? "Playing" : "Waiting"}</span>
                      </td>
                      <td>{l.hostName}</td>
                      <td>
                        {l.players}/{l.maxPlayers}
                        {l.spectators > 0 && <span className="muted"> +{l.spectators}</span>}
                      </td>
                      <td>{l.ante}</td>
                      <td className="mono">{l.code}</td>
                      <td>
                        <button
                          type="button"
                          className="btn small primary"
                          disabled={joining !== null}
                          onClick={() => void join(l)}
                          data-testid="lobby-join"
                        >
                          {joining === l.id ? "…" : "Join"}
                        </button>
                        {me.isAdmin && (
                          <button
                            type="button"
                            className="btn small danger lobby-close"
                            onClick={() => setClosing({ lobby: l, busy: false, error: null })}
                            aria-label={`Close ${l.name} (admin)`}
                            title="Admin: close this lobby for everyone"
                            data-testid="lobby-admin-close"
                          >
                            Close
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <aside className="welcome-side">
            <form className="group-box room-code" onSubmit={(e) => void joinByCode(e)} noValidate>
              <h2>Join by room code</h2>
              <div className="inline-field">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  maxLength={ROOM_CODE_LENGTH}
                  placeholder="ABC123"
                  aria-label="Room code"
                  className="mono code-input"
                  data-testid="room-code"
                />
                <button type="submit" className="btn primary" data-testid="room-code-join">
                  Join
                </button>
              </div>
              {codeError && (
                <p className="form-error" role="alert">
                  {codeError}
                </p>
              )}
            </form>
            {onVoiceTable && (
              <div className="group-box">
                <h2>Voice channel table</h2>
                <button
                  type="button"
                  className="btn primary big"
                  disabled={voiceTableBusy}
                  onClick={() => {
                    setVoiceTableBusy(true);
                    setVoiceTableError(null);
                    void onVoiceTable().then((problem) => {
                      setVoiceTableBusy(false);
                      setVoiceTableError(problem);
                    });
                  }}
                  data-testid="menu-voice-table"
                >
                  Join your channel&apos;s table
                </button>
                {voiceTableError && (
                  <p className="form-error" role="alert">
                    {voiceTableError}
                  </p>
                )}
              </div>
            )}
            <div className="group-box">
              <h2>Host a game</h2>
              <button type="button" className="btn primary big" onClick={() => setWin({ kind: "create" })} data-testid="menu-create-lobby">
                <Icon name="plus" /> Create a game
              </button>
            </div>
            <button type="button" className="btn link" onClick={() => setWin({ kind: "settings", tab: "howto" })} data-testid="how-to-play-button">
              New here? Read How to Play
            </button>
            <MenuTip />
          </aside>
        </div>
      </div>

      {win.kind === "settings" && (
        <SettingsWindow
          initialTab={win.tab}
          onClose={() => setWin({ kind: "none" })}
          account={{ me, onMeChange, onDeleted: onLogout }}
        />
      )}
      {win.kind === "shop" && <ShopWindow me={me} onMeChange={onMeChange} onClose={() => setWin({ kind: "none" })} />}
      {win.kind === "locker" && <LockerWindow me={me} onMeChange={onMeChange} onClose={() => setWin({ kind: "none" })} />}
      {win.kind === "create" && <CreateGameWindow onClose={() => setWin({ kind: "none" })} onCreate={create} />}
      {closing && (
        <Modal title={`Close “${closing.lobby.name}”?`} onClose={() => setClosing(null)} testId="admin-close-lobby">
          <p>
            Everyone in it ({closing.lobby.players} {closing.lobby.players === 1 ? "player" : "players"}
            {closing.lobby.spectators > 0 ? `, ${closing.lobby.spectators} watching` : ""}) goes back to the menu. A hand on the table is settled
            first (finished hands are paid, unfinished bets are refunded).
          </p>
          {closing.error && (
            <p className="form-error" role="alert">
              {closing.error}
            </p>
          )}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={() => setClosing(null)}>
              Cancel
            </button>
            <button type="button" className="btn danger" disabled={closing.busy} onClick={() => void closeLobby()} data-testid="admin-close-lobby-yes">
              {closing.busy ? "Closing…" : "Close lobby"}
            </button>
          </div>
        </Modal>
      )}
      {privateLobby && <JoinPrivateDialog lobby={privateLobby} onClose={() => setPrivateLobby(null)} onJoin={joinPrivate} />}
    </main>
  );
}
